<template>
  <registration-layout
    ref="form"
    :show-stepper="false"
  >
    <form
      class="new-registration vue"
      @submit.prevent="submit"
    >
      <section id="preapprovals-heading">
        <h2 id="preapprovals-title">
          {{ $t('pages.preapprovals.page_title') }}
        </h2>
      </section>
      <div
        v-if="quotes.length > 0"
        id="preapprovals-body"
      >
        <section id="quote-slider-section">
          <quotes-slider
            :quotes="quotes"
            :amount="maxAmountRequested"
            @sliderChanged="sliderChanged"
          />
        </section>
        <section
          v-if="selectedQuote"
          id="quote-details-section"
          class="alternate-background-color"
        >
          <h3
            class="loan-details-heading"
          >
            {{ $t('pages.preapprovals.loan_details_heading') }}
          </h3>
          <PreapprovalDetails
            :quote="selectedQuote"
          />
          <div id="consent-and-submit">
            <RegistrationNextButton
              ref="submitButton"
              :text="$t('pages.preapprovals.submit_text')"
              :use-spinner="true"
            />
          </div>
        </section>
      </div>
    </form>
  </registration-layout>
</template>

<script>
import RegistrationLayout from '../layouts/RegistrationLayout.vue';
import QuotesSlider from '../components/QuoteSlider.vue';
import PreapprovalDetails from '../components/PreapprovalDetails.vue';
import RegistrationNextButton from '../components/RegistrationNextButton.vue';
import railsAxios from '../lib/railsAxios';
import { trackPageSubmit } from '../tracking/adobe';

export default {
  components: {
    // eslint-disable-next-line max-len
    QuotesSlider,
    PreapprovalDetails,
    RegistrationNextButton,
    RegistrationLayout,
  },
  data() {
    return {
      amountRequested:       '',
      installmentsRequested: 0,
      selectedQuoteIndex:    0, // index of filtered Quote. Set to 0 on any change.
      quoteData:             {},
    };
  },
  computed: {
    quotes() {
      return this.lodash.sortBy(this.quoteData, ['amount_requested', 'installments_requested']);
    },
    maxAmountRequested() {
      return Math.max(...this.quotes.map((quote) => quote.amount_requested), 0);
    },
    filteredQuotes() {
      const filterParams = {
        amount_requested:       this.amountRequested,
        installments_requested: this.installmentsRequested,
      };

      const quotes = this.lodash.filter(this.quotes, filterParams);
      return this.lodash.sortBy(quotes, 'interest_rate');
    },
    selectedQuote() {
      return this.filteredQuotes[this.selectedQuoteIndex];
    },
  },
  mounted() {
    try {
      railsAxios.get(`/preapproved/${this.$route.params.id}/quotes`)
        .then((response) => {
          this.quoteData = response.data.quotes;
        });
      this.flash(this.$t('pages.preapprovals.welcome_message'), 'success');
    } catch (error) {
      // Data from call not required
      // No need to flash error message here
      // Logging happens in railsAxios
    }
  },
  methods: {
    sliderChanged(event) {
      this.amountRequested = event.amount.toString();
      this.installmentsRequested = event.installments;
    },
    async submit() {
      this.flash().destroyAll();
      await this.$store.dispatch('setAwaitingNavigation', true);

      try {
        const payload = {
          quote: {
            amount:      this.selectedQuote.amount_requested,
            installment: this.selectedQuote.installments_requested,
            ...this.selectedQuote,
          },
        };

        const response = await railsAxios.post('/preapproved/submit', payload);

        if (response.data.redirect) {
          trackPageSubmit('preapprovals');
          window.location.href = response.data.location;
          return;
        }

        await this.$store.dispatch('setAwaitingNavigation', false);
        this.flash(this.$t('shared.generic_error'), 'error');
      } catch (error) {
        await this.$store.dispatch('setAwaitingNavigation', false);
        this.flash(this.$t('shared.generic_error'), 'error');
        window.location = '/';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #nav-buttons {
    width: 100%;
    margin-top: 1rem;
  }
  .alternate-background-color {
    background-color: #f3f3f3;
  }
  #quote-slider-section {
    padding: 1rem;
    .quote-slider {
      width: 100%;
      box-shadow: 0px 3px 3px 1px rgba(0, 0, 0, 0.2);
      margin-bottom: 2rem;
    }
  }
  h3 {
    color: #05a006;
    margin-bottom: 1.5rem;
    text-align: center;
    font-size: 1.25rem;
    font-family: "Bree Serif", serif;
    font-weight: 400;
  }
  #consent-and-submit {
    margin-top: 3em;
    margin-bottom: 2em;
  }
  #submit-button {
    width: 100%;
    margin-top: 1rem;
    border-radius: 25px;
    font-size: 1rem;
    font-family: "Open Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif;
    font-weight: 600;
    min-height: 80px;
    .loader {
      width: 24px;
      height: 24px;
    }
  }
  #preapprovals-heading {
    padding: 0 1rem;
  }
  #preapprovals-title {
    font-size: 2rem;
    line-height: 1.1;
    font-family: "Bree Serif", serif;
    margin-bottom: 1.25rem;
    padding-top: 2rem;
  }
  #preapprovals-instructions {
    text-align: center;
    font-size: 1.4em;
    margin-bottom: 1rem;
  }
  #opt-in-card-section {
    padding: 2em 1rem;
    margin: 0 -4px 1rem -4px;
  }
  .opt-in-quote {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    font-weight: 400;
    text-align: center;
    margin: 0 auto;
    min-height: 225px;
    .first-installment-amount {
      font-weight: bold;
      font-size: 2.5em;
    }
    .interest-rate {
      margin-bottom: 1.5em;
      font-size: .8em;
    }
  }
  #quote-details-section {
    padding: 1rem;

    h3.loan-details-heading.opted-out {
      color: #e21111;
      border: 4px solid #e21111;
      border-radius: 1rem;
      padding: .5em;
      position: relative;
    }

    .tooltip-container {
      position: absolute;
      right: 3px;
      bottom: 3px;
    }
  }
  #direct-debit-dialog {
    font-size: 1.25em;
    #direct-debit-dialog-container {
      padding: 3em;
    }
    #direct-debit-dialog-instructions {
      margin-bottom: 2em;
    }
    .button-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      flex-direction: column;
      button {
        white-space: normal;
        display: block;
        height: auto;
        padding: 0.5em 1em;
        margin: 1em;
        text-transform: initial;
      }
      .secondary-button {
        background-color: white;
        border: 1px solid green;
        color: green;
      }
      .primary-button {
        background-color: rgb(245, 191, 5);
        color: #4a4a4a;
      }
    }
  }

  @media screen and (min-width: 700px) {
    .alternate-background-color {
      background-color: #fff;
    }
    #preapprovals-title {
      padding-top: 0;
      font-size: 2rem;
      padding-top: 1rem;
    }
    #preapprovals-instructions {
      font-size: 1.3em;
      margin-bottom: 2rem;
    }
    #preapprovals-body {
      display: grid;
      width: 100%;
      grid-template-columns: 55% 45%;
    }
    #opt-in-card-section {
      padding: 0;
      min-height: 300px;
    }
    #quote-details-section {
      padding: 0;
      grid-column-start: 2;

      .quote-table tr.highlighted {
        background-color: #f3f3f3;
        td {
          line-height: 2;
        }
      }

      #submit-button {
        border-radius: 75px;
      }
    }
    .opt-in-quote {
      margin-top: 1em;
      justify-content: flex-start;
      min-height: 206px;
      &:hover {
        cursor: pointer;
      }
      .first-installment-amount {
        font-size: 1.5em;
      }
      p {
        font-size: 13px;
        line-height: 1.5;
      }
      span {
        line-height: 1.5;
      }
    }
    .brazil-card {
      align-items: normal;
    }
    h3 {
      text-align: left;
      margin-bottom: 1rem;
    }
    .quote-slider {
      width: 100%;
      padding: 18px;
      box-shadow: 10px 3px 23px 2px rgba(0, 0, 0, 0.13);
      border-radius: 14px;
      border: 1px solid #c5c5c5;
    }
    #quote-slider-section {
      .quote-slider {
        width: 100%;
        padding: 18px;
        border-radius: 14px;
        border: 1px solid #c5c5c5;
      }
    }
    #preapprovals-heading {
      margin: 0 auto;
    }
    #direct-debit-dialog {
      #direct-debit-dialog-container {
        padding: 2.5em;
      }
      p {
        font-size: 1.25em;
      }
      .button-container {
        flex-direction: row;
        button {
          padding: 0.5em 1em;
          margin: 1em;
          width: 45%;
        }
      }
    }
  }
</style>
